import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Droplets, Printer, ClipboardCopy, Phone, Check } from "lucide-react";
import { PageHeader } from "./fordon";
import { DampAiScanner } from "@/components/DampAiScanner";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/fuktprotokoll";

/** De 12 punkter vi själva mäter i verkstaden i Kalix vid varje inbyte. */
const POINTS = [
  { id: "p1", label: "Front, ovankant vindruta/luftare", why: "Läcker först vid takluckans front och limfogen mot hytten." },
  { id: "p2", label: "Taklucka fram – kant runt karm", why: "Tätmassan torkar och spricker efter 6–8 år." },
  { id: "p3", label: "Taklucka bak – kant runt karm", why: "Samma som fram, men syns sällan från insidan." },
  { id: "p4", label: "Hörn vänster fram (golv/vägg)", why: "Vatten vandrar nedåt och samlas i hörnprofilen." },
  { id: "p5", label: "Hörn höger fram (golv/vägg)", why: "Jämför med vänster – stor skillnad tyder på punktläckage." },
  { id: "p6", label: "Fönsterkarm kök", why: "Öppnas ofta, gångjärn drar in fukt i väggskivan." },
  { id: "p7", label: "Fönsterkarm sovdel", why: "Kondens bakom madrass upptäcks sent." },
  { id: "p8", label: "Golv vid entrédörr", why: "Slitage + snö/väta ger de högsta värdena här." },
  { id: "p9", label: "Golv i duschutrymme", why: "Duschvatten under golvmatta är den vanligaste dyra skadan." },
  { id: "p10", label: "Vägg bakom kylskåpsgaller", why: "Ventilationsgallren är genomföringar rakt genom väggen." },
  { id: "p11", label: "Hörn vänster bak (golv/vägg)", why: "Bakvägg + stötfångarinfästning rör sig i kyla." },
  { id: "p12", label: "Hörn höger bak (golv/vägg)", why: "Kontrollera även under sängbrunn/garage." },
] as const;

const FAQ = [
  {
    q: "Vilka fuktvärden är okej i en husvagn eller husbil?",
    a: "Under 15 % är torrt och normalt. 15–20 % är förhöjt och ska kontrolleras om – ofta säsongsfukt eller kondens. Över 20 % räknas som fuktskada: trä börjar mörkna och limfogar släpper. Över 25 % är nästan alltid en skada som kräver öppning av konstruktionen.",
  },
  {
    q: "Hur många punkter ska ett fuktprotokoll innehålla?",
    a: "Vi mäter alltid minst 12 punkter: takluckor fram och bak, fyra hörn, två fönsterkarmar, golv vid dörr, golv i dusch, vägg bakom kylgaller och fronten. Ett protokoll med tre punkter säger nästan ingenting om fordonet.",
  },
  {
    q: "Kan en fuktmätare visa fel?",
    a: "Ja. Kapacitiva mätare påverkas av metall, folie, kablage och av att fordonet just tvättats. Mät alltid när fordonet varit torrt inomhus i minst ett dygn, jämför symmetriska punkter mot varandra och mät om en avvikande punkt två gånger innan du drar slutsatser.",
  },
  {
    q: "Vad kostar det att åtgärda en fuktskada?",
    a: "Ombyggnad av tätning och en mindre vägg­skiva ligger ofta på 8 000–20 000 kr. Golv- eller hörnskada med demontering av inredning hamnar oftast på 35 000–90 000 kr. Därför är protokollet före köpet det billigaste du kan göra.",
  },
  {
    q: "Får jag med protokollet när jag köper hos er?",
    a: "Ja. Varje fordon vi säljer levereras med fuktprotokoll, och du kan följa mätningen på plats i Kalix om du vill se värdena själv.",
  },
] as const;

export const Route = createFileRoute("/verktyg/fuktprotokoll")({
  head: () => ({
    meta: [
      { title: "Fuktprotokoll husvagn & husbil – 12 mätpunkter | AK Husbil" },
      {
        name: "description",
        content:
          "Fyll i dina fuktvärden på 12 mätpunkter och få direkt besked: torrt, förhöjt eller fuktskada. Gränsvärden, riskkostnad och färdigt protokoll att skriva ut.",
      },
      {
        name: "keywords",
        content: "fuktprotokoll husvagn, fuktmätning husbil, fuktvärden husvagn, fukttest gränsvärden, fuktskada husvagn kostnad",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Fuktprotokoll – fyll i 12 mätpunkter och få verkstadens besked" },
      {
        property: "og:description",
        content: "Verkstadens egna gränsvärden. Fyll i procenten, se vilka punkter som är röda och skriv ut protokollet.",
      },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Gör ett fuktprotokoll på husvagn eller husbil",
          description: "Så mäter och dokumenterar du fukt på 12 punkter innan du köper.",
          totalTime: "PT20M",
          tool: [{ "@type": "HowToTool", name: "Kapacitiv fuktmätare för trä" }],
          step: [
            { "@type": "HowToStep", name: "Förbered", text: "Fordonet ska ha stått torrt inomhus i minst ett dygn. Kalibrera mätaren mot en känd torr yta." },
            { "@type": "HowToStep", name: "Mät 12 punkter", text: "Takluckor fram och bak, fyra hörn, två fönsterkarmar, golv vid dörr, golv i dusch, vägg bakom kylgaller och fronten." },
            { "@type": "HowToStep", name: "Jämför symmetriskt", text: "Ställ vänster mot höger hörn. Skillnader över 4 procentenheter pekar på punktläckage." },
            { "@type": "HowToStep", name: "Tolka värdena", text: "Under 15 % torrt, 15–20 % förhöjt och ska mätas om, över 20 % fuktskada." },
            { "@type": "HowToStep", name: "Dokumentera", text: "Skriv ut protokollet med datum, fordon och alla värden och spara det till affären." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

type Verdict = "empty" | "dry" | "watch" | "damage";

function verdictFor(v: number | null): Verdict {
  if (v == null || Number.isNaN(v)) return "empty";
  if (v < 15) return "dry";
  if (v <= 20) return "watch";
  return "damage";
}

const VERDICT_STYLE: Record<Verdict, string> = {
  empty: "border-border bg-card text-muted-foreground",
  dry: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  watch: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  damage: "border-destructive/40 bg-destructive/10 text-destructive",
};

const VERDICT_LABEL: Record<Verdict, string> = {
  empty: "–",
  dry: "Torrt",
  watch: "Förhöjt",
  damage: "Fuktskada",
};

function Page() {
  const [vehicle, setVehicle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(
    () =>
      POINTS.map((p) => {
        const raw = (values[p.id] ?? "").replace(",", ".").trim();
        const num = raw === "" ? null : Number(raw);
        return { ...p, num, verdict: verdictFor(num) };
      }),
    [values],
  );

  const filled = parsed.filter((p) => p.num != null);
  const reds = parsed.filter((p) => p.verdict === "damage");
  const yellows = parsed.filter((p) => p.verdict === "watch");
  const highest = filled.reduce<number | null>((acc, p) => (acc == null || (p.num ?? 0) > acc ? p.num! : acc), null);

  // Symmetrikontroll: vänster mot höger hörn fram och bak.
  const asymmetry = useMemo(() => {
    const get = (id: string) => parsed.find((p) => p.id === id)?.num ?? null;
    const pairs: Array<[string, string, string]> = [
      ["p4", "p5", "hörnen fram"],
      ["p11", "p12", "hörnen bak"],
    ];
    return pairs
      .map(([a, b, label]) => {
        const av = get(a);
        const bv = get(b);
        if (av == null || bv == null) return null;
        const diff = Math.abs(av - bv);
        return diff >= 4 ? { label, diff: Math.round(diff * 10) / 10 } : null;
      })
      .filter(Boolean) as Array<{ label: string; diff: number }>;
  }, [parsed]);

  const summary = useMemo(() => {
    if (filled.length === 0) {
      return {
        title: "Fyll i minst fyra punkter",
        text: "Börja med takluckorna och de fyra hörnen – det är där skador nästan alltid startar.",
        cost: null as string | null,
        tone: "empty" as Verdict,
      };
    }
    if (reds.length > 0) {
      return {
        title: `${reds.length} punkt${reds.length > 1 ? "er" : ""} över 20 % – fuktskada`,
        text:
          "Behandla det som en konstaterad skada. Kräv att konstruktionen öppnas eller att priset speglar full åtgärd. Köp inte på löftet om att \"det torkar ut\" – trä som legat över 20 % har redan tappat hållfasthet och limfogar släpper.",
        cost:
          reds.some((p) => ["p8", "p9", "p4", "p5", "p11", "p12"].includes(p.id))
            ? "Golv- eller hörnskada: räkna 35 000–90 000 kr med demontering av inredning."
            : "Vägg- eller takskada: räkna 8 000–20 000 kr om den tas i tid.",
        tone: "damage" as Verdict,
      };
    }
    if (yellows.length > 0) {
      return {
        title: `${yellows.length} punkt${yellows.length > 1 ? "er" : ""} i gult (15–20 %)`,
        text:
          "Förhöjt men inte dömt. Mät om samma punkter efter ett dygn inomhus och jämför med den symmetriska motsvarigheten. Står värdet kvar ska tätningarna göras om nu – det är billigt jämfört med en golvskada.",
        cost: "Ny tätning runt luckor och fönster: 3 000–7 000 kr.",
        tone: "watch" as Verdict,
      };
    }
    return {
      title: filled.length >= 12 ? "Alla 12 punkter under 15 % – torrt" : `${filled.length} punkter under 15 % – torrt så långt`,
      text:
        filled.length >= 12
          ? "Det här är ett friskt fordon fuktmässigt. Spara protokollet – det är värt riktiga pengar när du själv säljer vidare."
          : "Fortsätt med resterande punkter innan du drar en slutsats. Dusch och golv vid dörren är de som oftast avviker.",
      cost: null,
      tone: "dry" as Verdict,
    };
  }, [filled.length, reds, yellows]);

  const protocolText = useMemo(() => {
    const lines = [
      "FUKTPROTOKOLL",
      `Fordon: ${vehicle || "—"}`,
      `Datum: ${date}`,
      "",
      ...parsed.map((p, i) => `${i + 1}. ${p.label}: ${p.num != null ? `${p.num} % (${VERDICT_LABEL[p.verdict]})` : "—"}`),
      "",
      `Högsta värde: ${highest != null ? `${highest} %` : "—"}`,
      `Bedömning: ${summary.title}`,
      "Gränsvärden: <15 % torrt · 15–20 % förhöjt · >20 % fuktskada",
      "Mall: akhusbilfritid.se/verktyg/fuktprotokoll",
    ];
    return lines.join("\n");
  }, [parsed, vehicle, date, highest, summary.title]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(protocolText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard nekad – utskrift finns kvar */
    }
  }

  return (
    <main>
      <PageHeader
        label="Verktyg"
        title="Fuktprotokoll – 12 mätpunkter med verkstadens gränsvärden"
        sub="Fyll i procenten du mäter. Du får direkt besked per punkt, en samlad bedömning, riskkostnad och ett protokoll du kan skriva ut och ta med till affären. Samma mall som vi själva använder vid inbyte i Kalix."
      />

      <section className="container-edge pb-16">
        <div className="grid gap-8 lg:grid-cols-[1.4fr,1fr]">
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Fordon</span>
                <input
                  value={vehicle}
                  onChange={(e) => setVehicle(e.target.value)}
                  placeholder="t.ex. Kabe Royal 780, 2016"
                  className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Mätdatum</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
                />
              </label>
            </div>

            <ol className="mt-7 space-y-3">
              {parsed.map((p, i) => (
                <li key={p.id} className={`rounded-2xl border p-4 transition ${VERDICT_STYLE[p.verdict]}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-display text-[15px] leading-snug text-foreground">
                        {i + 1}. {p.label}
                      </p>
                      <p className="mt-1 text-xs leading-snug text-muted-foreground">{p.why}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <input
                        inputMode="decimal"
                        value={values[p.id] ?? ""}
                        onChange={(e) => setValues((s) => ({ ...s, [p.id]: e.target.value }))}
                        placeholder="%"
                        aria-label={`Fuktvärde ${p.label}`}
                        className="w-20 rounded-xl border border-border bg-background px-3 py-2 text-right text-sm"
                      />
                      <span className="w-20 text-right text-xs font-medium">{VERDICT_LABEL[p.verdict]}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
              >
                <Printer size={15} /> Skriv ut protokollet
              </button>
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium"
              >
                {copied ? <Check size={15} /> : <ClipboardCopy size={15} />} {copied ? "Kopierat" : "Kopiera som text"}
              </button>
              <button
                type="button"
                onClick={() => setValues({})}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Nollställ
              </button>
            </div>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <div className={`rounded-3xl border p-6 ${VERDICT_STYLE[summary.tone]}`}>
              <p className="flex items-center gap-2 text-xs uppercase tracking-wide">
                <Droplets size={14} /> Bedömning
              </p>
              <h2 className="mt-2 font-display text-2xl leading-tight text-foreground">{summary.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{summary.text}</p>
              {summary.cost && (
                <p className="mt-3 rounded-xl border border-border bg-background/70 p-3 text-sm text-foreground/85">
                  {summary.cost}
                </p>
              )}
              <dl className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl border border-border bg-background/70 p-3">
                  <dt className="text-[11px] uppercase text-muted-foreground">Ifyllt</dt>
                  <dd className="font-display text-lg text-foreground">{filled.length}/12</dd>
                </div>
                <div className="rounded-xl border border-border bg-background/70 p-3">
                  <dt className="text-[11px] uppercase text-muted-foreground">Högsta</dt>
                  <dd className="font-display text-lg text-foreground">{highest != null ? `${highest} %` : "–"}</dd>
                </div>
                <div className="rounded-xl border border-border bg-background/70 p-3">
                  <dt className="text-[11px] uppercase text-muted-foreground">Röda</dt>
                  <dd className="font-display text-lg text-foreground">{reds.length}</dd>
                </div>
              </dl>
            </div>

            {asymmetry.length > 0 && (
              <div className="rounded-3xl border border-amber-500/40 bg-amber-500/10 p-6">
                <h2 className="font-display text-lg text-foreground">Osymmetriska värden</h2>
                <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                  {asymmetry.map((a) => (
                    <li key={a.label}>
                      {a.diff} procentenheters skillnad mellan {a.label} – tyder på punktläckage, inte allmän kondens.
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-3xl border border-border bg-warm p-6">
              <h2 className="font-display text-lg">Gränsvärden vi går efter</h2>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li><strong className="text-foreground">Under 15 %</strong> – torrt, normalt trä.</li>
                <li><strong className="text-foreground">15–20 %</strong> – förhöjt, mät om efter ett dygn inomhus.</li>
                <li><strong className="text-foreground">Över 20 %</strong> – fuktskada, konstruktionen ska öppnas.</li>
                <li><strong className="text-foreground">Över 25 %</strong> – röta är sannolik, avstå eller räkna full renovering.</li>
              </ul>
              <a
                href="tel:+46923500"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm"
              >
                <Phone size={14} /> Osäker på ett värde? Ring verkstaden
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section id="fuktskanner" className="container-edge pb-16 scroll-mt-24">
        <DampAiScanner />
      </section>

      <section className="container-edge pb-16">
        <div className="grid gap-8 md:grid-cols-2">
          <article className="rounded-3xl border border-border bg-card p-7">
            <h2 className="font-display text-2xl">Så mäter du så att värdena betyder något</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              En fuktmätare mäter ledningsförmåga, inte vatten. Därför spelar förutsättningarna större roll än
              själva siffran. Fordonet ska ha stått torrt och inte tvättats det senaste dygnet, och du ska mäta
              med samma tryck och samma mätare på alla punkter. Metall, folieisolering och kablage bakom skivan
              kan ge falska toppar – mät om 5 cm bredvid innan du drar slutsatsen.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Det starkaste verktyget är symmetri. Vänster och höger hörn är byggda likadant, så ett fordon med
              12 % till vänster och 19 % till höger har ett läckage – även om båda värdena i sig ser hanterbara ut.
              Samma logik gäller takluckorna fram mot bak.
            </p>
          </article>
          <article className="rounded-3xl border border-border bg-card p-7">
            <h2 className="font-display text-2xl">Vad protokollet är värt i affären</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Ett daterat protokoll med 12 punkter flyttar diskussionen från känsla till siffror. Som köpare får du
              en grund för prisavdrag eller för att gå därifrån. Som säljare är det ofta det enskilt starkaste
              argumentet du har: en husvagn med dokumenterat torra värden säljs snabbare och närmare utgångspriset
              än en identisk vagn utan protokoll.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Spara protokollet varje år. Tre års mätserier som ligger stilla under 15 % är bevis på att tätningarna
              sköts – och det syns direkt i värderingen.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-sm">
              <Link to="/fukttest-husbil" className="rounded-full border border-border px-4 py-2 hover:border-accent/50">
                Fukttest steg för steg
              </Link>
              <Link to="/vardera-husbil" className="rounded-full border border-border px-4 py-2 hover:border-accent/50">
                Värdera din husbil
              </Link>
              <Link to="/besiktning-husbil" className="rounded-full border border-border px-4 py-2 hover:border-accent/50">
                Besiktning av husbil
              </Link>
              <Link to="/fordon" className="rounded-full border border-border px-4 py-2 hover:border-accent/50">
                Fordon i lager
              </Link>
            </div>
          </article>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-3xl">Vanliga frågor om fuktprotokoll</h2>
          <dl className="mt-6 divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card">
            {FAQ.map((f) => (
              <div key={f.q} className="p-6">
                <dt className="font-display text-lg">{f.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </main>
  );
}
